import { formatDate, toCalendarDate } from '@/utils'
import { useStatements } from '../../hooks/project'
import type {
  SecuritiesAccount,
  SecuritiesActivity,
  SecuritiesHolding,
  SecuritiesRealizedGain,
  SecuritiesSectionCheck,
  Statement,
  StatementSegment,
} from '../../stores/types'
import type {
  Holding,
  PortfolioAccount,
  PortfolioActivity,
  PortfolioPeriod,
  RealizedGain,
  RowSource,
  SectionValidation,
} from './types'

// The engine sends account_type lowercase; the badge wants the capitalized label. Anything
// unknown lands on 'Investment' so a securities account still shows up instead of being dropped.
const toAccountType = (
  accountType: string | null,
): PortfolioAccount['accountType'] =>
  accountType?.toLowerCase() === 'retirement' ? 'Retirement' : 'Investment'

const periodLabel = (segment: StatementSegment): string => {
  if (segment.endDate) return formatDate(segment.endDate, 'MMM yyyy')
  if (segment.startDate) return formatDate(segment.startDate, 'MMM yyyy')
  return segment.name ?? 'Statement period'
}

// The period key the dropdown selects on and the chart reads as `${period}-01`. A month key
// (YYYY-MM) off the segment's end date keeps the x-axis real. A dateless segment falls back to
// its id, which still keys the dropdown uniquely (it just won't plot a dated point).
const periodKey = (segment: StatementSegment): string => {
  const periodDate = segment.endDate ?? segment.startDate
  if (periodDate) return formatDate(periodDate, 'yyyy-MM')
  return `segment-${segment.id}`
}

// Row ids must be globally unique across the flattened tables. One segment can hold many securities
// accounts, each re-indexing its rows from 0, so the segment id alone collides between accounts.
// The account last-4 disambiguates them.
const rowPrefix = (segment: StatementSegment, last4: string): string =>
  `segment-${segment.id}-${last4}`

// The source document a securities row came from. The page is per-row; the file name and URL come
// from the segment's parent statement, so every table can link back to the exact page.
const sourceOf = (
  statement: Statement,
  sourcePage: number | null,
): RowSource => ({
  statementFileName: statement.fileName,
  statementFileUrl: statement.fileUrl,
  sourcePage,
})

const toHolding = (
  holding: SecuritiesHolding,
  segment: StatementSegment,
  statement: Statement,
  index: number,
  last4: string,
): Holding => ({
  id: `${rowPrefix(segment, last4)}-holding-${index}`,
  symbol: holding.symbol ?? '',
  description: holding.description ?? '',
  assetClass: holding.assetClass ?? '',
  quantity: holding.quantity,
  price: holding.marketPrice,
  marketValue: holding.marketValue,
  costBasis: holding.costBasis,
  unrealizedGainLoss: holding.unrealizedGainLoss,
  ...sourceOf(statement, holding.sourcePage),
})

const toActivity = (
  activity: SecuritiesActivity,
  segment: StatementSegment,
  statement: Statement,
  index: number,
  last4: string,
): PortfolioActivity => ({
  id: `${rowPrefix(segment, last4)}-activity-${index}`,
  // Keep the canonical YYYY-MM-DD so the table sorts and the date filter compares right.
  date: activity.date ? toCalendarDate(activity.date) : '',
  type: activity.activityType ?? '',
  description: activity.description ?? '',
  symbol: activity.symbol ?? undefined,
  quantity: activity.quantity ?? undefined,
  amount: activity.cashAmount,
  ...sourceOf(statement, activity.sourcePage),
})

const toRealizedGain = (
  gain: SecuritiesRealizedGain,
  segment: StatementSegment,
  statement: Statement,
  index: number,
  last4: string,
): RealizedGain => ({
  id: `${rowPrefix(segment, last4)}-gain-${index}`,
  description: gain.description ?? '',
  symbol: gain.symbol ?? undefined,
  quantity: gain.quantity ?? undefined,
  liquidationDate: gain.liquidationDate
    ? toCalendarDate(gain.liquidationDate)
    : '',
  saleAmount: gain.saleAmount,
  costBasis: gain.costBasis,
  gainLoss: gain.gainLoss,
  term: gain.term ?? undefined,
  ...sourceOf(statement, gain.sourcePage),
})

// Only the engine's own failed verdict counts. A section the statement never printed a total for
// carries matches: false by design and is informational, not a failure. Older stored payloads
// have no status, so a printed total that did not match is the failure signal there.
const isFailed = (check: SecuritiesSectionCheck): boolean =>
  check.status == null
    ? check.printedTotal != null && !check.matches
    : check.status === 'failed'

const failingSections = (account: SecuritiesAccount): SectionValidation[] =>
  account.checks.filter(isFailed).map((check) => ({
    section: check.section,
    matches: check.matches,
    delta: check.delta,
    anchorMissing: check.anchorMissing,
  }))

// A segment that carries at least one securities account, paired with its parent statement so the
// rows can link back to the source file.
interface SecuritiesSegment {
  statement: Statement
  segment: StatementSegment
}

const securitiesSegments = (statements: Statement[]): SecuritiesSegment[] =>
  statements.flatMap((statement) =>
    (statement.segments ?? [])
      .filter(
        (segment) =>
          (segment.securitiesDetail?.accounts?.length ?? 0) > 0 &&
          segment.duplicateOfSegmentId == null,
      )
      .map((segment) => ({ statement, segment })),
  )

// A period plus the segment date it sorts on. The sort date is stripped before returning so the
// public shape stays PortfolioPeriod.
type DatedPeriod = PortfolioPeriod & { sortDate: string }

// An account being assembled across segments, holding dated periods so they can be ordered first.
type AccountAccumulator = Omit<PortfolioAccount, 'periods'> & {
  periods: DatedPeriod[]
}

// Fold statements -> segments -> securities accounts into the PortfolioAccount[] the tables consume.
// Each account groups across every segment by its last-4; each segment is one period. Read-only.
export const useProjectSecurities = (): PortfolioAccount[] => {
  const statements = useStatements()

  const accountsByKey = new Map<string, AccountAccumulator>()

  for (const { statement, segment } of securitiesSegments(statements)) {
    const securitiesAccounts = segment.securitiesDetail?.accounts ?? []
    // The brokerage brand is stamped once per statement, not per account. The account's own label is
    // the customer's name, so older envelopes without the brand get a generic one.
    const institution = segment.securitiesDetail?.institution ?? 'Brokerage'

    for (const securitiesAccount of securitiesAccounts) {
      const last4 = securitiesAccount.accountLast4
      // The last-4 is the account. The brand is free text that varies between statements, so it
      // only separates accounts that printed no last-4 at all.
      const accountKey = last4 || `${institution}-unknown`

      const period: DatedPeriod = {
        period: periodKey(segment),
        label: periodLabel(segment),
        // The segment date the period sorts on (end preferred, then start). A blank string sorts first.
        sortDate: segment.endDate ?? segment.startDate ?? '',
        holdings: securitiesAccount.holdings.map((holding, index) =>
          toHolding(holding, segment, statement, index, last4),
        ),
        activity: securitiesAccount.activity.map((activity, index) =>
          toActivity(activity, segment, statement, index, last4),
        ),
        realizedGains: securitiesAccount.realizedGains.map((gain, index) =>
          toRealizedGain(gain, segment, statement, index, last4),
        ),
      }

      const existing = accountsByKey.get(accountKey)
      if (existing) {
        existing.periods.push(period)
        // Keep the account validated only while every one of its segments validated.
        existing.validated = existing.validated && securitiesAccount.validated
        existing.failingSections.push(...failingSections(securitiesAccount))
        continue
      }

      accountsByKey.set(accountKey, {
        id: accountKey,
        accountNumber: last4,
        institution,
        accountType: toAccountType(securitiesAccount.accountType),
        validated: securitiesAccount.validated,
        failingSections: failingSections(securitiesAccount),
        periods: [period],
      })
    }
  }

  // Periods arrive in fetch order, not chronological. Sort each account's periods oldest -> newest
  // so the selector defaults to the latest and the chart reads left to right, then drop the sort date.
  return Array.from(accountsByKey.values()).map((account) => ({
    ...account,
    periods: account.periods
      .sort((first, second) => first.sortDate.localeCompare(second.sortDate))
      .map(({ sortDate: _sortDate, ...period }) => period),
  }))
}
